import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/outline';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useServices } from '../../context/ServiceContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseTimeToDate, normalizeTimeDate, formatTimeForApi } from '../../utils/time';

const serviceSchema = yup.object().shape({
  name: yup.string().required('Service name is required'),
  service_category_id: yup.string(),
  description: yup.string(),
  code: yup.string(),
  media_url: yup
  .string()
  .test('is-valid-url', 'Media URL must be a valid URL', (value) => {
    if (!value) return true; // skip empty strings, use .required() if needed
    try {
      const url = new URL(value);
      return ['http:', 'https:'].includes(url.protocol);
    } catch (err) {
      return false;
    }
  }),
  active: yup.boolean(),
  rates: yup.array().of(
    yup.object().shape({
      rate_name: yup.string().required('Rate name is required'),
      qty_hrs: yup.number().required('Quantity/Hours is required'),
      regular_rate: yup.number().required('Regular rate is required'),
      regular_rate_total: yup.number(),
      member_rate: yup.number().required('Member rate is required'),
      member_rate_total: yup.number(),
      addon_rate: yup.number(),
      addon_rate_total: yup.number(),
      premium_rate: yup.number(),
      premium_rate_total: yup.number(),
      value_rate: yup.number(),
      value_rate_total: yup.number(),
      after_hours_rate: yup.number(),
      after_hours_rate_total: yup.number(),
      is_pays_commission: yup.boolean(),
      fixed_commission_amount: yup.number().when('is_pays_commission', {
        is: true,
        then: (schema) => schema.min(0, 'Fixed commission must be 0 or greater'),
        otherwise: (schema) => schema.notRequired()
      }),
      percentage_commission: yup
      .number()
      .nullable()
      .when('is_pays_commission', {
        is: true,
        then: (schema) =>
          schema
            .min(0, 'Percentage commission must be 0 or greater')
            .max(100, 'Percentage commission cannot exceed 100%'),
        otherwise: (schema) => schema.notRequired(),
      }),
      is_pays_bonus: yup.boolean(),
      fixed_bonus_amount: yup
        .number()
        .nullable()
        .when('is_pays_bonus', {
          is: true,
          then: (schema) => schema.min(0, 'Fixed bonus must be 0 or greater'),
          otherwise: (schema) => schema.notRequired(),
        }),
    percentage_bonus: yup
      .number()
      .nullable()
      .when('is_pays_bonus', {
        is: true,
        then: (schema) =>
          schema
            .min(0, 'Percentage bonus must be 0 or greater')
            .max(100, 'Percentage bonus cannot exceed 100%'),
        otherwise: (schema) => schema.notRequired(),
      }),
      internal_cost: yup.number(),
      task_time: yup.string()
    })
  ),
  products: yup.array().of(
    yup.object().shape({
      id: yup.string(),
      qty: yup.number().required('Quantity is required'),
      regular_price: yup.number().required('Regular price is required'),
      member_price: yup.number().required('Member price is required')
    })
  ),
  portal_options: yup.object().shape({
    is_available_in_portal: yup.boolean(),
    first_available_time: yup.mixed(),
    last_available_time: yup.mixed(),
    arrival_time_window: yup.string()
  })
});

const ServiceForm = ({ embedded = false, onSuccess, onCancel } = {}) => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = embedded ? undefined : routeId;
  const { addService, updateService, getServiceById, getServiceAdditionalData } = useServices();
  const { showLoading, hideLoading } = useLoading();
  const isEdit = !!id;
  const [imagePreviews, setImagePreviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [showAdditionalRates, setShowAdditionalRates] = useState(false);
  const getRoutePath = useRoutePath();
  const categoryRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
     
    return {
      name: data.name || '',
      service_category_id: data.service_category_id || '',
      description: data.description || '',
      code: data.code || '',
      media_url: data.media_url || '',
      active: data.active ?? true,
      rates: data.rates || [{
        rate_name: '',
        qty_hrs: 0,
        regular_rate: 0,
        regular_rate_total: 0,
        member_rate: 0,
        member_rate_total: 0,
        addon_rate: 0,
        addon_rate_total: 0,
        premium_rate: 0,
        premium_rate_total: 0,
        value_rate: 0,
        value_rate_total: 0,
        after_hours_rate: 0,
        after_hours_rate_total: 0,
        is_pays_commission: false,
        fixed_commission_amount: 0,
        percentage_commission: 0,
        is_pays_bonus: false,
        fixed_bonus_amount: 0,
        percentage_bonus: 0,
        internal_cost: 0,
        task_time: '00:00'
      }],
      products: data.products || [{
        id: '',
        qty: 0,
        regular_price: 0,
        member_price: 0
      }],
      portal_options: {
        is_available_in_portal: data.portal_options?.is_available_in_portal ?? false,
        first_available_time: parseTimeToDate(data.portal_options?.first_available_time || '09:00'),
        last_available_time: parseTimeToDate(data.portal_options?.last_available_time || '17:00'),
        arrival_time_window: data.portal_options?.arrival_time_window || '1'
      },
      image_removed_ids: data.image_removed_ids || []
    };
  }, []);

  const [getEditData, setGetEditData] = useState(null);
  const hasFetched = useRef(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch, setError, reset } = useForm({
    resolver: yupResolver(serviceSchema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });
 
  const fetchServiceData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getServiceById(id);
        if (response?.status === 200) {
          setGetEditData(response.data);
          // Reset form with fetched data
          reset(getDefaultValues(response.data));
          // Set image previews if any
          if (response.data.display_images && response.data.display_images.length > 0) {
            setImagePreviews(response.data.display_images);
          }
        }
      } catch (error) {
        console.error('Error fetching service data:', error);
        toast.error('Error fetching service data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getServiceById, showLoading, hideLoading, reset, getDefaultValues]);

  useEffect(() => {
    fetchServiceData();
  }, [fetchServiceData]);

  // Load categories and products from API
  useEffect(() => {
    const loadAdditionalData = async () => {
      try {
        const additionalData = await getServiceAdditionalData();
        if (additionalData?.serviceCategories) {
          setCategories(additionalData.serviceCategories.data || []);
        }
        if (additionalData?.products) {
          setProducts(additionalData.products.data || []);
        }
      } catch (error) {
        console.error('Error loading additional data:', error);
      }
    };

    if (categoryRef.current === null) {
      loadAdditionalData();
      categoryRef.current = true;
    }
  }, [getServiceAdditionalData]);

  useEffect(() => {
    if(!id){
      reset(getDefaultValues()); 
      setImagePreviews([])
    }
  }, [id, reset, getDefaultValues]);

  const handleInputChange = (e, index = null, field = null) => {
    if (!e) return;

    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    if (field === 'rates') {
      const updatedRates = [...watch('rates')];
      if (name.includes('.')) {
        // Handle nested field names like "rates.0.addon_rate"
        const parts = name.split('.');
        if (parts.length >= 3) {
          // Extract the actual field name (e.g., "addon_rate" from "rates.0.addon_rate")
          const fieldName = parts.slice(2).join('.');
          updatedRates[index][fieldName] = type === 'number' ? parseFloat(value) || 0 : value;
        } else {
          const fieldName = parts[1];
          updatedRates[index][fieldName] = type === 'number' ? parseFloat(value) || 0 : value;
        }
      } else {
        updatedRates[index][name] = type === 'number' ? parseFloat(value) || 0 : value;
      }
      setValue('rates', updatedRates);
    } else if (field === 'products') {
      const updatedProducts = [...watch('products')];
      if (name.includes('.')) {
        const parts = name.split('.');
        if (parts.length >= 3) {
          const fieldName = parts.slice(2).join('.');
          updatedProducts[index][fieldName] = type === 'number' ? parseFloat(value) || 0 : value;
          
          // If product selection changed, auto-populate prices
          if (fieldName === 'id' && value) {
            const selectedProduct = products.find(p => p.id.toString() === value.toString());
            if (selectedProduct) {
              updatedProducts[index].regular_price = parseFloat(selectedProduct.regular_price) || 0;
              updatedProducts[index].member_price = parseFloat(selectedProduct.member_price) || 0;
            }
          }
        } else {
          const fieldName = parts[1];
          updatedProducts[index][fieldName] = type === 'number' ? parseFloat(value) || 0 : value;
        }
      } else {
        updatedProducts[index][name] = type === 'number' ? parseFloat(value) || 0 : value;
      }
      setValue('products', updatedProducts);
    } else if (name && name.includes('.')) {
      const [parent, child] = name.split('.');
      setValue(`${parent}.${child}`, fieldValue);
    } else {
      setValue(name, fieldValue);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/svg+xml'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} has an invalid file type.`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`${file.name} exceeds the 10MB size limit.`);
        return false;
      }
      return true;
    });
 
    if(validFiles.length === 0){
      return;
    }

    const newPreviews = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      created_at: new Date().toISOString(),
      url: URL.createObjectURL(file)
    }));
 
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This image will be removed from the service',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
    }).then((result) => {
      if (result.isConfirmed) {
        const currentRemoveIds = watch('image_removed_ids') || []; // Fallback to empty array
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setValue('image_removed_ids', [...currentRemoveIds, imagePreviews[index].id]);
      }
    });
    
  };

  const addRate = () => {
    setValue('rates', [...watch('rates'), {
      rate_name: '',
      qty_hrs: 0,
      regular_rate: 0,
      regular_rate_total: 0,
      member_rate: 0,
      member_rate_total: 0,
      addon_rate: 0,
      addon_rate_total: 0,
      premium_rate: 0,
      premium_rate_total: 0,
      value_rate: 0,
      value_rate_total: 0,
      after_hours_rate: 0,
      after_hours_rate_total: 0,
      is_pays_commission: false,
      fixed_commission_amount: 0,
      percentage_commission: 0,
      is_pays_bonus: false,
      fixed_bonus_amount: 0,
      percentage_bonus: 0,
      internal_cost: 0,
      task_time: '00:00'
    }]);
  };

  const removeRate = (index) => {
    const newRates = [...watch('rates')];
    newRates.splice(index, 1);
    setValue('rates', newRates);
  };

  const addProduct = () => {
    setValue('products', [...watch('products'), {
      id: '',
      qty: 0,
      regular_price: 0,
      member_price: 0
    }]);
  };

  const removeProduct = (index) => {
    const newProducts = [...watch('products')];
    newProducts.splice(index, 1);
    setValue('products', newProducts);
  };


  
  

  const calculateTotal = (rate, qty_hrs) => {
    return (parseFloat(rate) || 0) * (parseFloat(qty_hrs) || 0);
  };

  const calculateRatesTotal = (rateType) => {
    const rates = watch('rates') || [];
    return rates.reduce((total, rate) => {
      const rateValue = parseFloat(rate[rateType]) || 0;
      const qty_hrs = parseFloat(rate.qty_hrs) || 0;
      return total + (rateValue * qty_hrs);
    }, 0);
  };

  
  const calculateProductsTotal = (priceType) => {
    const products = watch('products') || [];
    return products.reduce((total, product) => {
      const price = parseFloat(product[priceType]) || 0;
      const qty = parseFloat(product.qty) || 0;
      return total + (price * qty);
    }, 0);
  };



  const onSubmit = async (data) => {
    
    showLoading();
    setIsLoading(true);
    try {
      const serviceData = {
        ...data,
        images: imagePreviews,
        portal_options: {
          ...data.portal_options,
          first_available_time: formatTimeForApi(data.portal_options?.first_available_time),
          last_available_time: formatTimeForApi(data.portal_options?.last_available_time),
        }
      };

      if (id) {
        const response = await updateService(id, serviceData);
        if (response.status === 200) {
          setIsLoading(false);
          toast.success('Service updated successfully!');
          if (embedded && onSuccess) {
            onSuccess(response);
          } else {
            navigate(getRoutePath('/services'));
          }
        } else {
          handleErrorResponse(response);
        }
      } else {
        setIsLoading(false);
        const response = await addService(serviceData);
        if (response.status === 200) {
          toast.success('Service added successfully!');
          if (embedded && onSuccess) {
            onSuccess(response);
          } else {
            navigate(getRoutePath('/services'));
          }
        } else {
          setIsLoading(false);
          handleErrorResponse(response);
        }
      }
     
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while saving the service');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };

  const handleErrorResponse = (response) => {
   
    if (response.errors) {
            
      Object.entries(response.errors).forEach(([field, message]) => {
        // Extract actual message
        let errorMessage = '';

        if (Array.isArray(message)) {
          errorMessage = message[0]; // handle array case
        } else if (typeof message === 'object' && message !== null) {
          errorMessage = Object.values(message)[0]; // take the first value from the object
        } else {
          errorMessage = String(message); // fallback
        }

        setError(field, {
          type: 'manual',
          message: errorMessage,
        });

      
        toast.error(`${field}: ${errorMessage}`);
        
      });
    } else if (response.error?.message) {
      setError('general', {
        type: 'manual',
        message: response.error.message,
      });
    
    }
  };

  return (
    <div className={embedded ? '' : 'container mx-auto px-4 py-8'}>
      {!embedded && <Breadcrumb  />}
      {!embedded && (
        <h2 className="text-2xl font-bold mb-6">{isEdit ? 'Edit Service' : 'Create New Service'}</h2>
      )}
      <div className={`bg-white ${embedded ? '' : 'rounded-lg shadow-md p-6'}`}>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
              <input
                type="text"
                {...register('name', {
                  required: 'Service name is required',
                  onChange: (e) => handleInputChange(e)
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter Service Name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service Category</label>
              <select
                {...register('service_category_id', {
                  onChange: (e) => handleInputChange(e)
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.service_category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
                value={watch('service_category_id')}
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
              {errors.service_category_id && (
                <p className="mt-1 text-sm text-red-600">{errors.service_category_id.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                {...register('description')}
                onChange={(e) => handleInputChange(e)}
                rows="3"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter Description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                {...register('code', {
                  onChange: (e) => handleInputChange(e)
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.code ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter Code"
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Media URL</label>
              <input
                type="text"
                {...register('media_url', {
                  onChange: (e) => handleInputChange(e)
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.media_url ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter Media URL"
              />
              {errors.media_url && (
                <p className="mt-1 text-sm text-red-600">{errors.media_url.message}</p>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('active')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                checked={watch('active')}
                onChange={(e) => setValue('active', e.target.checked)}
              />
              <label className="ml-2 block text-sm text-gray-900">Active</label>
            </div>

            {/* Image Upload Section */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
              <div className="flex items-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500
 file:mr-4 file:py-2 file:px-4
 file:rounded-md file:border-0
 file:text-sm file:font-semibold
 file:bg-blue-50 file:text-blue-700
 hover:file:bg-blue-100"
                  id="image-upload"
                  placeholder="Upload Images"
                />

              </div>
             
              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-3 max-h-[400px] overflow-y-auto md:max-h-none">

                  {imagePreviews.map((image, index) =>
                    image.status !== 404 && (
                      <div
                        key={index}
                        className="relative group overflow-hidden rounded-lg"
                      >
                        <img
                          src={image?.path ? image.path : image?.url}
                          alt={`Product preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />

                        {/* Black transparent overlay on hover */}
                        <div className="absolute inset-0 bg-black/0  group-hover:bg-black/40 transition duration-300 rounded-lg"></div>

                        {/* View button centered */}
                        <Link
                          to={image?.path ? image.path : image?.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity z-10"
                        >
                          <span className="bg-gray-200 text-black px-3 py-1 rounded hover:bg-gray-200 transition">
                          <EyeIcon className="h-6 w-6 bg-gray-200 text-black p-1 rounded hover:bg-gray-200 transition" />
                          </span>
                        </Link>

                        {/* Trash icon in top-right */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
              
            </div>
          </div>

          {/* Rates Section */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Rates</h3>
              <button
                type="button"
                onClick={addRate}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Rate
              </button>
            </div>
            {watch('rates')?.map((_, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 mb-6 relative">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate Name</label>
                    <input
                      type="text"
                      {...register(`rates.${index}.rate_name`, {
                        required: 'Rate name is required',
                        onChange: (e) => handleInputChange(e, index, 'rates')
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.rate_name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter Rate Name"
                    />
                    {errors.rates?.[index]?.rate_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.rates[index].rate_name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qty/Hrs</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`rates.${index}.qty_hrs`, {
                        required: 'Quantity/Hours is required',
                        onChange: (e) => {
                          handleInputChange(e, index, 'rates');
                          // Update all total fields when qty/hrs changes
                          const qty_hrs = parseFloat(e.target.value) || 0;
                          const regular_rate = parseFloat(watch(`rates.${index}.regular_rate`)) || 0;
                          const member_rate = parseFloat(watch(`rates.${index}.member_rate`)) || 0;
                          const addon_rate = parseFloat(watch(`rates.${index}.addon_rate`)) || 0;
                          const premium_rate = parseFloat(watch(`rates.${index}.premium_rate`)) || 0;
                          const value_rate = parseFloat(watch(`rates.${index}.value_rate`)) || 0;
                          const after_hours_rate = parseFloat(watch(`rates.${index}.after_hours_rate`)) || 0;
                          
                          setValue(`rates.${index}.regular_rate_total`, regular_rate * qty_hrs);
                          setValue(`rates.${index}.member_rate_total`, member_rate * qty_hrs);
                          setValue(`rates.${index}.addon_rate_total`, addon_rate * qty_hrs);
                          setValue(`rates.${index}.premium_rate_total`, premium_rate * qty_hrs);
                          setValue(`rates.${index}.value_rate_total`, value_rate * qty_hrs);
                          setValue(`rates.${index}.after_hours_rate_total`, after_hours_rate * qty_hrs);
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.qty_hrs ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.rates?.[index]?.qty_hrs && (
                      <p className="mt-1 text-sm text-red-600">{errors.rates[index].qty_hrs.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Regular Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`rates.${index}.regular_rate`, {
                        required: 'Regular rate is required',
                        onChange: (e) => {
                          handleInputChange(e, index, 'rates');
                          // Update the total field
                          const total = calculateTotal(e.target.value, watch(`rates.${index}.qty_hrs`));
                          setValue(`rates.${index}.regular_rate_total`, total);
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.regular_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter Regular Rate"
                    />
                    {errors.rates?.[index]?.regular_rate && (
                      <p className="mt-1 text-sm text-red-600">{errors.rates[index].regular_rate.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Regular Rate Total</label>
                    <input
                      type="number"
                      {...register(`rates.${index}.regular_rate_total`)}
                      value={calculateTotal(watch(`rates.${index}.regular_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Member Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register(`rates.${index}.member_rate`, {
                        required: 'Member rate is required',
                        onChange: (e) => {
                          handleInputChange(e, index, 'rates');
                          // Update the total field
                          const total = calculateTotal(e.target.value, watch(`rates.${index}.qty_hrs`));
                          setValue(`rates.${index}.member_rate_total`, total);
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.member_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter Member Rate"
                    />
                    {errors.rates?.[index]?.member_rate && (
                      <p className="mt-1 text-sm text-red-600">{errors.rates[index].member_rate.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Member Rate Total</label>
                    <input
                      type="number"
                      {...register(`rates.${index}.member_rate_total`)}
                      value={calculateTotal(watch(`rates.${index}.member_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    />
                  </div>
                </div>

                {/* Additional Rates Section */}
                <div className="border-t pt-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setShowAdditionalRates(!showAdditionalRates)}
                    className="text-blue-600 hover:text-blue-800 underline text-sm font-medium"
                  >
                    {showAdditionalRates ? 'Hide Additional Rates' : 'Show Additional Rates'}
                  </button>
                  
                  {showAdditionalRates && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add-On Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={watch(`rates.${index}.addon_rate`) || ''}
                          onChange={(e) => {
                            console.log('Add-On Rate changed:', e.target.value);
                            const newValue = parseFloat(e.target.value) || 0;
                            setValue(`rates.${index}.addon_rate`, newValue);
                            const total = calculateTotal(newValue, watch(`rates.${index}.qty_hrs`));
                            setValue(`rates.${index}.addon_rate_total`, total);
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.addon_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Add-On Rate"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Add-On Rate Total</label>
                        <input
                          type="number"
                          step="0.01"
                          readOnly
                          value={calculateTotal(watch(`rates.${index}.addon_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Premium Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={watch(`rates.${index}.premium_rate`) || ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setValue(`rates.${index}.premium_rate`, newValue);
                            const total = calculateTotal(newValue, watch(`rates.${index}.qty_hrs`));
                            setValue(`rates.${index}.premium_rate_total`, total);
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.premium_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Premium Rate"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Premium Rate Total</label>
                        <input
                          type="number"
                          step="0.01"
                          readOnly
                          value={calculateTotal(watch(`rates.${index}.premium_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Value Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={watch(`rates.${index}.value_rate`) || ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setValue(`rates.${index}.value_rate`, newValue);
                            const total = calculateTotal(newValue, watch(`rates.${index}.qty_hrs`));
                            setValue(`rates.${index}.value_rate_total`, total);
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.value_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Value Rate"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Value Rate Total</label>
                        <input
                          type="number"
                          step="0.01"
                          readOnly
                          value={calculateTotal(watch(`rates.${index}.value_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Afterhours Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={watch(`rates.${index}.after_hours_rate`) || ''}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value) || 0;
                            setValue(`rates.${index}.after_hours_rate`, newValue);
                            const total = calculateTotal(newValue, watch(`rates.${index}.qty_hrs`));
                            setValue(`rates.${index}.after_hours_rate_total`, total);
                          }}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.after_hours_rate ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Afterhours Rate"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Afterhours Rate Total</label>
                        <input
                          type="number"
                          step="0.01"
                          readOnly
                          value={calculateTotal(watch(`rates.${index}.after_hours_rate`), watch(`rates.${index}.qty_hrs`)).toFixed(2)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Commission Section */}
                <div className="border-t pt-4 mb-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      {...register(`rates.${index}.is_pays_commission`)}
                      onChange={(e) => {
                        const newRates = [...watch('rates')];
                        newRates[index].is_pays_commission = e.target.checked;
                        setValue('rates', newRates);
                        if (e.target.checked) {
                          newRates[index].fixed_commission_amount = 0;
                          newRates[index].percentage_commission = 0;
                          setValue(`rates.${index}.fixed_commission_amount`, 0);
                          setValue(`rates.${index}.percentage_commission`, 0);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm font-medium text-gray-900">Pays Commission</label>
                  </div>
                  {watch(`rates.${index}.is_pays_commission`) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Commission ($)</label>
                        <input
                          type="number"
                          {...register(`rates.${index}.fixed_commission_amount`)}
                          onChange={(e) => handleInputChange(e, index, 'rates')}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.fixed_commission_amount ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Fixed Commission"
                        />
                        {errors.rates?.[index]?.fixed_commission_amount && (
                          <p className="mt-1 text-sm text-red-600">{errors.rates[index].fixed_commission_amount.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage Commission (%)</label>
                        <input
                          type="number"
                          {...register(`rates.${index}.percentage_commission`)}
                          onChange={(e) => handleInputChange(e, index, 'rates')}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.percentage_commission ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Percentage Commission"
                        />
                        {errors.rates?.[index]?.percentage_commission && (
                          <p className="mt-1 text-sm text-red-600">{errors.rates[index].percentage_commission.message}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bonus Section */}
                <div className="border-t pt-4 mb-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      {...register(`rates.${index}.is_pays_bonus`)}
                      onChange={(e) => {
                        const newRates = [...watch('rates')];
                        newRates[index].is_pays_bonus = e.target.checked;
                        setValue('rates', newRates);
                        if (e.target.checked) {
                          newRates[index].fixed_bonus_amount = 0;
                          newRates[index].percentage_bonus = 0;
                          setValue(`rates.${index}.fixed_bonus_amount`, 0);
                          setValue(`rates.${index}.percentage_bonus`, 0);
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm font-medium text-gray-900">Pays Bonus</label>
                  </div>
                  {watch(`rates.${index}.is_pays_bonus`) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Bonus ($)</label>
                        <input
                          type="number"
                          {...register(`rates.${index}.fixed_bonus_amount`)}
                          onChange={(e) => handleInputChange(e, index, 'rates')}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.fixed_bonus_amount ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Fixed Bonus"
                        />
                        {errors.rates?.[index]?.fixed_bonus_amount && (
                          <p className="mt-1 text-sm text-red-600">{errors.rates[index].fixed_bonus_amount.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Percentage Bonus (%)</label>
                        <input
                          type="number"
                          {...register(`rates.${index}.percentage_bonus`)}
                          onChange={(e) => handleInputChange(e, index, 'rates')}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.rates?.[index]?.percentage_bonus ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter Percentage Bonus"
                        />
                        {errors.rates?.[index]?.percentage_bonus && (
                          <p className="mt-1 text-sm text-red-600">{errors.rates[index].percentage_bonus.message}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Other Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Internal Cost</label>
                    <input
                      type="number"
                      {...register(`rates.${index}.internal_cost`)}
                      onChange={(e) => handleInputChange(e, index, 'rates')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.internal_cost ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter Internal Cost"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Task Time</label>
                    <input
                      type="time"
                      {...register(`rates.${index}.task_time`)}
                      onChange={(e) => handleInputChange(e, index, 'rates')}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.rates?.[index]?.task_time ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter Task Time"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => removeRate(index)}
                  className="absolute top-2 right-2 p-2 text-red-600 hover:bg-red-100 rounded-full"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))}

           
          </div>

          {/* Products Section */}
          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Products/Materials</h3>
              <button
                type="button"
                onClick={addProduct}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Product
              </button>
            </div>
            {watch('products')?.map((_, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 relative">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                  <select
                    {...register(`products.${index}.id`, {
                      required: 'Product is required',
                      onChange: (e) => handleInputChange(e, index, 'products')
                    })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.products?.[index]?.id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Select Product"
                    value={watch(`products.${index}.id`) || ''}
                  >
                    <option value="">Select Product</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} - ${product.regular_price || 0}
                      </option>
                    ))}
                  </select>
                  {errors.products?.[index]?.id && (
                    <p className="mt-1 text-sm text-red-600">{errors.products[index].id.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qty</label>
                  <input
                    type="number"
                    {...register(`products.${index}.qty`, {
                      required: 'Quantity is required',
                      onChange: (e) => handleInputChange(e, index, 'products')
                    })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.products?.[index]?.qty ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter Quantity"
                  />
                  {errors.products?.[index]?.qty && (
                    <p className="mt-1 text-sm text-red-600">{errors.products[index].qty.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regular Price</label>
                  <input
                    type="number"
                    {...register(`products.${index}.regular_price`, {
                      required: 'Regular price is required'
                    })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.products?.[index]?.regular_price ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    } bg-gray-100 cursor-not-allowed`}
                    readOnly
                    placeholder="Auto-populated from product"
                  />
                  {errors.products?.[index]?.regular_price && (
                    <p className="mt-1 text-sm text-red-600">{errors.products[index].regular_price.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Member Price</label>
                  <input
                    type="number"
                    {...register(`products.${index}.member_price`, {
                      required: 'Member price is required'
                    })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.products?.[index]?.member_price ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    } bg-gray-100 cursor-not-allowed`}
                    readOnly
                    placeholder="Auto-populated from product"
                  />
                  {errors.products?.[index]?.member_price && (
                    <p className="mt-1 text-sm text-red-600">{errors.products[index].member_price.message}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeProduct(index)}
                  className="absolute top-0 right-0 p-2 text-red-600 hover:bg-red-100 rounded-full -mt-2"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            ))}
 
          </div>
          
          <div className="  justify-between items-center mt-4">
               {/* Overall Totals Section */}
            <div className="border-t pt-6 mt-6">
              <h4 className="text-lg font-medium mb-4">Overall Totals</h4>
              
              {/* Rates Totals */}
              <div className="mb-6">
                <h5 className="text-md font-medium mb-3 text-gray-800">Service Rates</h5>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-700">Total - Regular Rate</div>
                    <div className="text-xl font-bold text-gray-900">
                      ${calculateRatesTotal('regular_rate').toFixed(2)}
                      {setValue('total_service_regular_rates', calculateRatesTotal('regular_rate').toFixed(2))}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-700">Total - Member Rate</div>
                    <div className="text-xl font-bold text-gray-900">
                      ${calculateRatesTotal('member_rate').toFixed(2)}
                      {setValue('total_service_member_rates', calculateRatesTotal('member_rate').toFixed(2))}
                    </div>
                  </div>
                  {showAdditionalRates && (
                    <>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm font-medium text-gray-700">Total - Add-On Rate</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${calculateRatesTotal('addon_rate').toFixed(2)}
                          {setValue('total_service_addon_rates', calculateRatesTotal('addon_rate').toFixed(2))}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm font-medium text-gray-700">Total - Premium Rate</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${calculateRatesTotal('premium_rate').toFixed(2)}
                          {setValue('total_service_premium_rates', calculateRatesTotal('premium_rate').toFixed(2))}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm font-medium text-gray-700">Total - Value Rate</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${calculateRatesTotal('value_rate').toFixed(2)}
                          {setValue('total_service_value_rates', calculateRatesTotal('value_rate').toFixed(2))}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-sm font-medium text-gray-700">Total - Afterhours Rate</div>
                        <div className="text-xl font-bold text-gray-900">
                          ${calculateRatesTotal('after_hours_rate').toFixed(2)}
                          {setValue('total_service_after_hours_rates', calculateRatesTotal('after_hours_rate').toFixed(2))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Products Totals */}
              <div>
                <h5 className="text-md font-medium mb-3 text-gray-800">Products/Materials</h5>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-gray-700">Total - Regular Price</div>
                    <div className="text-xl font-bold text-blue-900">
                      ${calculateProductsTotal('regular_price').toFixed(2)}
                      {setValue('total_product_regular_price', calculateProductsTotal('regular_price').toFixed(2))}
                    </div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="text-sm font-medium text-gray-700">Total - Member Price</div>
                    <div className="text-xl font-bold text-blue-900">
                      ${calculateProductsTotal('member_price').toFixed(2)}
                      {setValue('total_product_member_price', calculateProductsTotal('member_price').toFixed(2))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Grand Totals */}
              <div className="mt-6 pt-4 border-t">
                <h5 className="text-md font-medium mb-3 text-gray-800">Grand Totals</h5>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="text-sm font-medium text-gray-700">Grand Total - Regular</div>
                    <div className="text-xl font-bold text-green-900">
                      ${(calculateRatesTotal('regular_rate') + calculateProductsTotal('regular_price')).toFixed(2)}
                      {setValue('grand_total_regular', (calculateRatesTotal('regular_rate') + calculateProductsTotal('regular_price')).toFixed(2))}
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="text-sm font-medium text-gray-700">Grand Total - Member</div>
                    <div className="text-xl font-bold text-green-900">
                      ${(calculateRatesTotal('member_rate') + calculateProductsTotal('member_price')).toFixed(2)}
                      {setValue('grand_total_member', (calculateRatesTotal('member_rate') + calculateProductsTotal('member_price')).toFixed(2))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Portal Options Section */}
          <div className="border-t pt-6">
            
            <h3 className="text-lg font-medium mb-4">Web Portal Options</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('portal_options.is_available_in_portal')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  placeholder="Available in Portal"
                />
                <label className="ml-2 block text-sm text-gray-900">Available in Portal</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Arrival Time Window Length</label>
                <select
                  {...register('portal_options.arrival_time_window')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.portal_options?.arrival_time_window ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Select Arrival Time Window Length"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(hours => (
                    <option key={hours} value={hours}>{hours} {hours === 1 ? 'hour' : 'hours'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  First Available Time
                </label>
                <DatePicker
                  selected={watch('portal_options.first_available_time') instanceof Date ? watch('portal_options.first_available_time') : null}
                  onChange={(date) => setValue('portal_options.first_available_time', normalizeTimeDate(date), { shouldValidate: true })}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="Select Start Time"
                  wrapperClassName="w-full"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    errors.portal_options?.first_available_time ? 'border-red-500' : ''
                  }`}
                />
                {errors.portal_options?.first_available_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.portal_options.first_available_time.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Last Available Time
                </label>
                <DatePicker
                  selected={watch('portal_options.last_available_time') instanceof Date ? watch('portal_options.last_available_time') : null}
                  onChange={(date) => setValue('portal_options.last_available_time', normalizeTimeDate(date), { shouldValidate: true })}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={30}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="Select End Time"
                  wrapperClassName="w-full"
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg ${
                    errors.portal_options?.last_available_time ? 'border-red-500' : ''
                  }`}
                />
                {errors.portal_options?.last_available_time && (
                  <p className="mt-1 text-sm text-red-600">{errors.portal_options.last_available_time.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            {isLoading ? (
              <div className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </div>
            ) : (
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
                {isEdit ? 'Update Service' : 'Create Service'}
              </button>
            )}
            {embedded && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm; 